import { Component, ElementRef, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import {
  ChallengeTemplateModel,
  CustomPagesProfileData,
  CustomPagesProfileModel,
  DefaultTemplates,
  OrganizationCustomPagesProfileService,
  PageTemplateModel,
} from '@shared/services/custom-page-profiles/organization-custom-pages-profile.service';
import {
  ERROR_CODE,
  ErrorCodeRecord,
  ErrorCodeRecordDescription,
  ErrorCodeRecordLabel,
} from '@shared/enums/errors/ErrorCodes';
import { ERROR_CONTENT_TYPE, ErrorTypeRegistry } from '@shared/enums/errors/ErrorContentTypes';
import {
  AbstractControl,
  FormBuilder,
  FormControl,
  FormGroup,
  FormsModule,
  PristineChangeEvent,
  ReactiveFormsModule,
  ValidationErrors,
  ValidatorFn,
  Validators,
} from '@angular/forms';
import { forkJoin, Subject, takeUntil } from 'rxjs';
import { UnsavedChangesService } from '@shared/services/unsaved-state/unsaved-changes.service';
import { DirtyStateService } from '@shared/services/dirty-state/dirty-state.service';
import { ToastrService } from '@shared/services/toastr.service';
import { SanitizeService } from '@shared/services/sanitize/sanitize.service';
import { ADD_ROUTE } from '../../../../app-routing.constants';
import { safeHtmlValidator } from '@shared/validators/dom-purify-validator';
import { SwitchComponent } from '@shared/components/switch/switch.component';
import { NgOptionComponent, NgSelectComponent } from '@ng-select/ng-select';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';

const MAX_BYTE_SIZE = 2048;

const CONTENT_TYPE_BY_MIME: Record<string, ERROR_CONTENT_TYPE> = Object.fromEntries(
  Object.entries(ErrorTypeRegistry).map(([key, mime]) => [mime, key as ERROR_CONTENT_TYPE]),
) as Record<string, ERROR_CONTENT_TYPE>;

const maxByteSize =
  (max: number): ValidatorFn =>
  (control: AbstractControl): ValidationErrors | null => {
    const size = new TextEncoder().encode(control.value ?? '').length;
    return size > max ? { maxByteSize: { max, actual: size } } : null;
  };

const jsonSyntax: ValidatorFn = (control: AbstractControl): ValidationErrors | null => {
  if (control.parent?.get('contentType')?.value !== ERROR_CONTENT_TYPE.JSON) {
    return null;
  }
  const value: string = control.value ?? '';
  if (!value.trim()) {
    return null;
  }
  try {
    JSON.parse(value);
    return null;
  } catch (e) {
    return { invalidJson: (e as Error).message };
  }
};

const atLeastOneTemplateActivated: ValidatorFn = (group: AbstractControl): ValidationErrors | null => {
  const activated = Object.keys(ErrorCodeRecord).some((key) => group.get([key, 'activate'])?.value);
  return activated ? null : { noTemplateActivated: true };
};

export interface ByteSizeViolation {
  max: number;
  actual: number;
}

export interface ErrorCardState {
  key: string;
  label: string;
  descriptionKey: string;
  challenge: boolean;
  activated: boolean;
  expanded: boolean;
  needsSync: boolean;
  desynced: boolean;
  invalid: boolean;
  syncing: boolean;
  syncDisabled: boolean;
  resetDisabled: boolean;
  sanitizedLabel: string | null;
  sanitized: boolean;
  fieldInvalidHeader: boolean;
  fieldInvalidFooter: boolean;
  fieldInvalidBody: boolean;
  unsafeHtmlHeader: string | null;
  unsafeHtmlFooter: string | null;
  unsafeHtmlBody: string | null;
  byteSizeHeader: ByteSizeViolation | null;
  byteSizeFooter: ByteSizeViolation | null;
  byteSizeBody: ByteSizeViolation | null;
  jsonError: string | null;
  backgroundColor: string;
  previewType: string;
  previewHtml: SafeHtml;
  previewText: string;
}

export interface ContentTypeOption {
  key: string;
  label: string;
}

@Component({
  selector: 'app-add-custom-pages-profile',
  templateUrl: './add-custom-pages-profile.html',
  styleUrl: './add-custom-pages-profile.scss',
  imports: [FormsModule, ReactiveFormsModule, SwitchComponent, NgSelectComponent, NgOptionComponent, TranslatePipe],
})
export class AddCustomPagesProfile implements OnInit, OnDestroy {
  customPagesProfileFormGroup: FormGroup;
  headerFormGroup: FormGroup;
  customPagesProfileModel: Partial<CustomPagesProfileData> & { uid?: string };

  editTitle = false;
  isCreation = false;
  customProfileSize: number = 0;

  protected cards: ErrorCardState[] = [];
  protected hasDesynced = false;
  protected saveDisabled = true;
  protected noTemplateActivated = false;

  protected challengeHeader: SafeHtml = '';
  protected challengeFooter: SafeHtml = '';
  protected challengeFocus: 'header' | 'footer' | null = null;

  protected readonly contentTypeOptions: ContentTypeOption[] = Object.entries(ErrorTypeRegistry).map(
    ([key, label]) => ({ key, label }),
  );

  private defaults: DefaultTemplates;

  private readonly errorKeys: string[] = Object.keys(ErrorCodeRecord);
  private readonly expandedCards = new Set<string>();
  private readonly syncedSignature = new Map<string, string>();
  private readonly sanitizedFields = new Map<string, string[]>();
  private readonly syncing = new Set<string>();
  private readonly previewBody = new Map<string, SafeHtml>();
  private readonly previewText = new Map<string, string>();
  private readonly previewContentType = new Map<string, string>();
  private readonly currentId: string;
  private readonly destroy$ = new Subject<void>();

  constructor(
    private activatedRoute: ActivatedRoute,
    private organizationCustomPagesProfileService: OrganizationCustomPagesProfileService,
    private fb: FormBuilder,
    private router: Router,
    private readonly dirtyState: DirtyStateService,
    private unsavedChangesService: UnsavedChangesService,
    private sanitizer: DomSanitizer,
    private sanitizeService: SanitizeService,
    private toastr: ToastrService,
    private translate: TranslateService,
  ) {
    const segments = this.activatedRoute.snapshot.url;
    this.currentId = segments[segments.length - 1].path;
  }

  @ViewChild('nameInput')
  set nameInput(input: ElementRef<HTMLInputElement> | undefined) {
    if (input && this.isCreation) {
      input.nativeElement.focus();
    }
  }

  get nameCtrl(): FormControl {
    return this.headerFormGroup.get('name') as FormControl;
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  ngOnInit(): void {
    this.isCreation = this.currentId === ADD_ROUTE;
    this.defaults = this.organizationCustomPagesProfileService.getDefaultTemplates();
    this.initForms();
    this.wireActivateToggles();
    this.wireExpansionOnActivate();
    this.wireFieldChanges();
    this.wireAutoSyncOnActivate();

    if (this.isCreation) {
      this.customPagesProfileModel = {};
      this.editTitle = true;
      this.loadCustomPagesProfiles();
    } else {
      this.getCurrentCustomization(this.currentId);
    }

    this.refresh();
  }

  protected trackByKey = (_index: number, card: ErrorCardState) => card.key;

  protected trackByOption = (_index: number, option: ContentTypeOption) => option.key;

  protected toggleExpand(key: string): void {
    if (this.expandedCards.has(key)) {
      this.expandedCards.delete(key);
    } else {
      this.expandedCards.add(key);
    }
    this.refresh();
  }

  protected setChallengeFocus(field: 'header' | 'footer' | null): void {
    this.challengeFocus = field;
  }

  protected resetTemplate(key: string): void {
    const group = this.customPagesProfileFormGroup.get(key) as FormGroup | null;
    if (!group || this.syncing.has(key) || !group.get('activate')?.value) return;

    if (this.isChallenge(key)) {
      const def = this.defaults.challengeTemplate;
      group.patchValue(
        {
          header: def.header ?? '',
          footer: def.footer ?? '',
          backgroundColor: def.backgroundColor ?? '#ffffff',
        },
        { emitEvent: false },
      );
    } else {
      const def = this.defaultTemplateFor(key);
      group.patchValue(
        {
          contentType: this.toContentTypeKey(def.contentType),
          body: def.body ?? '',
        },
        { emitEvent: false },
      );
    }

    this.afterReset(key, group);
  }

  protected sync(key: string): void {
    if (this.syncing.has(key) || this.computeInvalid(key)) return;
    const group = this.customPagesProfileFormGroup.get(key) as FormGroup;
    if (!group) return;

    if (!this.computeNeedsSync(key)) {
      this.refreshLocalPreview(key);
      this.refresh();
      return;
    }

    const raw = group.getRawValue();
    const requestedSignature = this.signature(key);
    this.syncing.add(key);
    this.refresh();

    if (this.isChallenge(key)) {
      forkJoin({
        header: this.sanitizeService.sanitizeBody(raw.header ?? ''),
        footer: this.sanitizeService.sanitizeBody(raw.footer ?? ''),
      })
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: ({ header, footer }) => {
            this.syncing.delete(key);
            if (this.signature(key) !== requestedSignature) {
              this.refresh();
              return;
            }

            const cleanHeader = header ?? '';
            const cleanFooter = footer ?? '';
            const changed: string[] = [];
            if ((raw.header ?? '') !== cleanHeader) changed.push(this.translate.instant('ErrorHandler.header'));
            if ((raw.footer ?? '') !== cleanFooter) changed.push(this.translate.instant('ErrorHandler.footer'));

            group.patchValue({ header: cleanHeader, footer: cleanFooter }, { emitEvent: false });
            group.get('header')!.updateValueAndValidity({ emitEvent: false });
            group.get('footer')!.updateValueAndValidity({ emitEvent: false });

            this.challengeHeader = this.sanitizer.bypassSecurityTrustHtml(cleanHeader);
            this.challengeFooter = this.sanitizer.bypassSecurityTrustHtml(cleanFooter);
            this.syncedSignature.set(key, this.signature(key));

            if (changed.length) {
              this.sanitizedFields.set(key, changed);
              this.dirtyState.setDirty();
            } else {
              this.sanitizedFields.delete(key);
            }
            this.refresh();
          },
          error: () => {
            this.syncing.delete(key);
            this.refresh();
          },
        });
      return;
    }

    this.sanitizeService
      .sanitizeBody(raw.body ?? '')
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (body) => {
          this.syncing.delete(key);
          if (this.signature(key) !== requestedSignature) {
            this.refresh();
            return;
          }

          const cleanBody = body ?? '';
          const changed = (raw.body ?? '') !== cleanBody;
          const bodyCtrl = group.get('body')!;

          bodyCtrl.setValue(cleanBody, { emitEvent: false });
          bodyCtrl.updateValueAndValidity({ emitEvent: false });

          this.setPreview(key, cleanBody, raw.contentType);
          this.syncedSignature.set(key, this.signature(key));

          if (changed) {
            this.sanitizedFields.set(key, [this.translate.instant('ErrorHandler.body')]);
            this.dirtyState.setDirty();
          } else {
            this.sanitizedFields.delete(key);
          }
          this.refresh();
        },
        error: () => {
          this.syncing.delete(key);
          this.refresh();
        },
      });
  }

  protected confirmTitle(): void {
    const value = this.nameCtrl.value?.trim();
    if (value && value != '') {
      this.customPagesProfileModel.name = value;
    } else {
      const defaultName = this.translate.instant('ErrorHandler.createProfile') + ' #' + this.customProfileSize;
      this.customPagesProfileModel.name = defaultName;
      this.nameCtrl.setValue(defaultName, { emitEvent: false });
    }
    this.editTitle = false;
    this.refresh();
  }

  protected async cancel(): Promise<void> {
    if (this.dirtyState.isDirty()) {
      const confirmed = await this.unsavedChangesService.confirm();
      if (!confirmed) {
        return;
      }
    }
    this.dirtyState.clear();
    this.router.navigate(['../..'], {
      relativeTo: this.activatedRoute,
      queryParams: { tab: 'errorHandling' },
    });
  }

  protected save(): void {
    if (this.errorKeys.some((key) => this.computeDesynced(key))) return;

    const formValue = this.customPagesProfileFormGroup.getRawValue();
    const { name, comment } = this.headerFormGroup.getRawValue();

    const origin = formValue[ERROR_CODE.SERVER_ERROR];
    const blocked = formValue[ERROR_CODE.FORBIDDEN];
    const challenge = formValue[ERROR_CODE.CHALLENGE];

    this.customPagesProfileModel = {
      ...this.customPagesProfileModel,
      name,
      comment,
      originErrorTemplate: origin.activate ? this.toPageTemplate(origin) : null,
      blockedTemplate: blocked.activate ? this.toPageTemplate(blocked) : null,
      challengeTemplate: challenge.activate ? this.toChallengeTemplate(challenge) : null,
    };

    const createOrUpdateResponse = this.isCreation
      ? this.organizationCustomPagesProfileService.createCustomPagesProfiles(
          this.toData(this.customPagesProfileModel as CustomPagesProfileModel),
        )
      : this.organizationCustomPagesProfileService.updateCustomPagesProfiles(
          this.customPagesProfileModel.uid,
          this.toData(this.customPagesProfileModel as CustomPagesProfileModel),
        );
    createOrUpdateResponse.pipe(takeUntil(this.destroy$)).subscribe(() => {
      this.dirtyState.clear();
      this.toastr.success();
      this.router.navigate(['../..'], {
        relativeTo: this.activatedRoute,
        queryParams: { tab: 'errorHandling' },
      });
    });
  }

  private afterReset(key: string, group: FormGroup): void {
    for (const control of Object.values(group.controls)) {
      control.updateValueAndValidity({ emitEvent: false });
    }
    group.updateValueAndValidity({ emitEvent: false });

    this.sanitizedFields.delete(key);
    this.dirtyState.setDirty();

    if (this.computeNeedsSync(key)) {
      this.sync(key);
    } else {
      this.refreshLocalPreview(key);
    }

    this.refresh();
  }

  private refresh(): void {
    if (!this.customPagesProfileFormGroup || !this.headerFormGroup) return;

    const states: ErrorCardState[] = [];
    for (const key of this.errorKeys) {
      const group = this.customPagesProfileFormGroup.get(key) as FormGroup | null;
      if (!group) continue;

      const challenge = this.isChallenge(key);
      const activated = !!group.get('activate')?.value;
      const invalid = this.computeInvalid(key);
      const desynced = this.computeDesynced(key);
      const syncing = this.syncing.has(key);
      const changedFields = this.sanitizedFields.get(key) ?? null;

      states.push({
        key,
        label: ErrorCodeRecordLabel[key],
        descriptionKey: ErrorCodeRecordDescription[key],
        challenge,
        activated,
        expanded: this.expandedCards.has(key),
        needsSync: this.computeNeedsSync(key),
        desynced,
        invalid,
        syncing,
        syncDisabled: !desynced || invalid || syncing,
        resetDisabled: !activated || syncing,
        sanitizedLabel: changedFields ? changedFields.join(', ') : null,
        sanitized: !!changedFields,
        fieldInvalidHeader: challenge && !!group.get('header')?.errors,
        fieldInvalidFooter: challenge && !!group.get('footer')?.errors,
        fieldInvalidBody: !challenge && !!group.get('body')?.errors,
        unsafeHtmlHeader: this.joinUnsafe(group, 'header'),
        unsafeHtmlFooter: this.joinUnsafe(group, 'footer'),
        unsafeHtmlBody: this.joinUnsafe(group, 'body'),
        byteSizeHeader: group.get('header')?.errors?.['maxByteSize'] ?? null,
        byteSizeFooter: group.get('footer')?.errors?.['maxByteSize'] ?? null,
        byteSizeBody: group.get('body')?.errors?.['maxByteSize'] ?? null,
        jsonError: group.get('body')?.errors?.['invalidJson'] ?? null,
        backgroundColor: group.get('backgroundColor')?.value ?? '#ffffff',
        previewType: this.previewContentType.get(key) ?? '',
        previewHtml: this.previewBody.get(key) ?? '',
        previewText: this.previewText.get(key) ?? '',
      });
    }

    this.cards = states;
    this.hasDesynced = states.some((state) => state.desynced);
    this.noTemplateActivated = this.customPagesProfileFormGroup.hasError('noTemplateActivated');
    this.saveDisabled = this.customPagesProfileFormGroup.invalid || this.headerFormGroup.invalid || this.hasDesynced;
  }

  private joinUnsafe(group: FormGroup, field: string): string | null {
    const violations: string[] | undefined = group.get(field)?.errors?.['unsafeHtml'];
    return violations?.length ? violations.join(', ') : null;
  }

  private isChallenge(key: string): boolean {
    return key === ERROR_CODE.CHALLENGE;
  }

  private computeNeedsSync(key: string): boolean {
    if (this.isChallenge(key)) {
      return true;
    }
    return this.customPagesProfileFormGroup?.get([key, 'contentType'])?.value === ERROR_CONTENT_TYPE.HTML;
  }

  private computeDesynced(key: string): boolean {
    if (!this.computeNeedsSync(key)) {
      return false;
    }
    if (!this.customPagesProfileFormGroup?.get([key, 'activate'])?.value) {
      return false;
    }
    return this.signature(key) !== this.syncedSignature.get(key);
  }

  private computeInvalid(key: string): boolean {
    const group = this.customPagesProfileFormGroup?.get(key) as FormGroup | null;
    if (!group?.get('activate')?.value) {
      return false;
    }
    const fields = this.isChallenge(key) ? ['header', 'footer'] : ['contentType', 'body'];
    return fields.some((field) => !!group.get(field)?.errors);
  }

  private setPreview(key: string, body: string, contentType: string): void {
    this.previewContentType.set(key, contentType);
    this.previewText.set(key, contentType === ERROR_CONTENT_TYPE.JSON ? this.prettyJson(body) : body);
    this.previewBody.set(
      key,
      contentType === ERROR_CONTENT_TYPE.HTML
        ? // nosemgrep: typescript.angular.security.audit.angular-domsanitizer.angular-bypasssecuritytrust
          this.sanitizer.bypassSecurityTrustHtml(body)
        : '',
    );
  }

  private prettyJson(raw: string): string {
    if (!raw) return '';
    try {
      return JSON.stringify(JSON.parse(raw), null, 2);
    } catch {
      return raw;
    }
  }

  private refreshLocalPreview(key: string): void {
    if (this.isChallenge(key)) return;
    const group = this.customPagesProfileFormGroup.get(key) as FormGroup | null;
    if (!group) return;

    const raw = group.getRawValue();
    this.setPreview(key, raw.body ?? '', raw.contentType);
    this.syncedSignature.set(key, this.signature(key));
  }

  private signature(key: string): string {
    const group = this.customPagesProfileFormGroup.get(key);
    if (!group) return '';
    const raw = group.getRawValue();
    return this.isChallenge(key)
      ? JSON.stringify([raw.header ?? '', raw.footer ?? ''])
      : JSON.stringify([raw.contentType ?? '', raw.body ?? '']);
  }

  private toData(profile: CustomPagesProfileModel): CustomPagesProfileData {
    return {
      name: profile.name,
      comment: profile.comment,
      blockedTemplate: profile.blockedTemplate,
      originErrorTemplate: profile.originErrorTemplate,
      challengeTemplate: profile.challengeTemplate,
    };
  }

  private toPageTemplate(value: { contentType: string; body: string }): PageTemplateModel {
    const key = (value.contentType || ERROR_CONTENT_TYPE.PLAIN) as ERROR_CONTENT_TYPE;
    return {
      contentType: ErrorTypeRegistry[key],
      body: value.body || null,
    };
  }

  private toChallengeTemplate(value: {
    header: string;
    footer: string;
    backgroundColor: string;
  }): ChallengeTemplateModel {
    return {
      header: value.header || null,
      footer: value.footer || null,
      backgroundColor: value.backgroundColor || null,
    };
  }

  private defaultTemplateFor(key: string): PageTemplateModel {
    return key === ERROR_CODE.FORBIDDEN ? this.defaults.blockedTemplate : this.defaults.originErrorTemplate;
  }

  private initForms(): void {
    this.headerFormGroup = this.fb.group({
      name: ['', Validators.required],
      comment: [''],
    });

    const groups: Record<string, FormGroup> = {};
    for (const key of this.errorKeys) {
      if (this.isChallenge(key)) {
        groups[key] = this.fb.group({
          activate: [false],
          header: [
            this.defaults.challengeTemplate.header ?? '',
            [maxByteSize(MAX_BYTE_SIZE), safeHtmlValidator('challenge')],
          ],
          footer: [
            this.defaults.challengeTemplate.footer ?? '',
            [maxByteSize(MAX_BYTE_SIZE), safeHtmlValidator('challenge')],
          ],
          backgroundColor: [this.defaults.challengeTemplate.backgroundColor ?? '#ffffff'],
        });
      } else {
        const def = this.defaultTemplateFor(key);
        groups[key] = this.fb.group({
          activate: [false],
          contentType: [this.toContentTypeKey(def.contentType), Validators.required],
          body: [def.body ?? '', [maxByteSize(MAX_BYTE_SIZE), safeHtmlValidator('page'), jsonSyntax]],
        });
      }
    }
    this.customPagesProfileFormGroup = this.fb.group(groups, { validators: atLeastOneTemplateActivated });

    this.headerFormGroup.events.pipe(takeUntil(this.destroy$)).subscribe((e) => {
      if (e instanceof PristineChangeEvent) {
        this.dirtyState.setDirty();
      }
      this.refresh();
    });
    this.customPagesProfileFormGroup.events.pipe(takeUntil(this.destroy$)).subscribe((e) => {
      if (e instanceof PristineChangeEvent) {
        this.dirtyState.setDirty();
      }
      this.refresh();
    });
  }

  private wireFieldChanges(): void {
    for (const key of this.errorKeys) {
      const group = this.customPagesProfileFormGroup.get(key) as FormGroup;

      if (this.isChallenge(key)) {
        for (const field of ['header', 'footer']) {
          group
            .get(field)!
            .valueChanges.pipe(takeUntil(this.destroy$))
            .subscribe(() => this.sanitizedFields.delete(key));
        }
        continue;
      }

      const body = group.get('body')!;
      const contentType = group.get('contentType')!;
      body.updateValueAndValidity({ emitEvent: false });

      body.valueChanges.pipe(takeUntil(this.destroy$)).subscribe(() => {
        this.sanitizedFields.delete(key);
        if (!this.computeNeedsSync(key)) {
          this.refreshLocalPreview(key);
        }
      });

      contentType.valueChanges.pipe(takeUntil(this.destroy$)).subscribe(() => {
        body.updateValueAndValidity();
        this.sanitizedFields.delete(key);
        if (!this.computeNeedsSync(key)) {
          this.refreshLocalPreview(key);
        }
      });

      if (!this.computeNeedsSync(key)) {
        this.refreshLocalPreview(key);
      }
    }
  }

  private wireAutoSyncOnActivate(): void {
    for (const key of this.errorKeys) {
      const activateCtrl = this.customPagesProfileFormGroup.get([key, 'activate']);
      if (!activateCtrl) continue;

      activateCtrl.valueChanges.pipe(takeUntil(this.destroy$)).subscribe((activated) => {
        if (activated && this.computeDesynced(key)) {
          this.sync(key);
        }
      });
    }
  }

  private wireActivateToggles(): void {
    for (const key of this.errorKeys) {
      const group = this.customPagesProfileFormGroup.get(key) as FormGroup;
      const activate = group.get('activate')!;
      const fields = this.isChallenge(key) ? ['header', 'footer', 'backgroundColor'] : ['contentType', 'body'];

      const toggle = (on: boolean) => {
        const opts = { emitEvent: false };
        for (const f of fields) {
          const ctrl = group.get(f)!;
          on ? ctrl.enable(opts) : ctrl.disable(opts);
        }
      };

      toggle(activate.value);
      activate.valueChanges.pipe(takeUntil(this.destroy$)).subscribe(toggle);
    }
  }

  private wireExpansionOnActivate(): void {
    for (const key of this.errorKeys) {
      const activateCtrl = this.customPagesProfileFormGroup.get([key, 'activate']);
      if (!activateCtrl) continue;

      if (activateCtrl.value) {
        this.expandedCards.add(key);
      }

      activateCtrl.valueChanges.pipe(takeUntil(this.destroy$)).subscribe((activated) => {
        if (activated) {
          this.expandedCards.add(key);
        } else {
          this.expandedCards.delete(key);
        }
      });
    }
  }

  private getCurrentCustomization(uid: string): void {
    this.organizationCustomPagesProfileService
      .getCustomPagesProfilesById(uid)
      .pipe(takeUntil(this.destroy$))
      .subscribe((content) => {
        this.customPagesProfileModel = content;
        this.patchFromCustomization();
      });
  }

  private loadCustomPagesProfiles() {
    this.organizationCustomPagesProfileService.getCustomPagesProfiles({ number: 0, size: 1 }).subscribe((page) => {
      this.customProfileSize = page.totalElements + 1;
      this.refresh();
    });
  }

  private patchFromCustomization(): void {
    this.headerFormGroup.patchValue({
      name: this.customPagesProfileModel?.name ?? '',
      comment: this.customPagesProfileModel?.comment ?? '',
    });

    this.patchPageTemplate(ERROR_CODE.SERVER_ERROR, this.customPagesProfileModel?.originErrorTemplate);
    this.patchPageTemplate(ERROR_CODE.FORBIDDEN, this.customPagesProfileModel?.blockedTemplate);
    this.patchChallengeTemplate(this.customPagesProfileModel?.challengeTemplate);

    for (const key of this.errorKeys) {
      if (!this.computeNeedsSync(key)) {
        this.refreshLocalPreview(key);
      } else if (this.computeDesynced(key)) {
        this.sync(key);
      }
    }

    this.refresh();
  }

  private patchPageTemplate(code: ERROR_CODE, template: PageTemplateModel | null | undefined): void {
    if (!template) return;
    const group = this.customPagesProfileFormGroup.get(code) as FormGroup | null;
    if (!group) return;

    group.patchValue(
      {
        contentType: this.toContentTypeKey(template.contentType),
        body: template.body ?? '',
      },
      { emitEvent: false },
    );
    group.get('body')!.updateValueAndValidity({ emitEvent: false });
    group.get('activate')!.setValue(true);
  }

  private patchChallengeTemplate(template: ChallengeTemplateModel | null | undefined): void {
    if (!template) return;
    const group = this.customPagesProfileFormGroup.get(ERROR_CODE.CHALLENGE) as FormGroup | null;
    if (!group) return;

    group.patchValue(
      {
        header: template.header ?? '',
        footer: template.footer ?? '',
        backgroundColor: template.backgroundColor ?? '#ffffff',
      },
      { emitEvent: false },
    );
    group.get('activate')!.setValue(true);
  }

  private toContentTypeKey(value: string | null | undefined): ERROR_CONTENT_TYPE {
    return (value && CONTENT_TYPE_BY_MIME[value]) || ERROR_CONTENT_TYPE.PLAIN;
  }
}
